<script setup lang="ts">
import type { PagesWithVideo } from '@/interfaces/internal-resources/TutorialVideo'
import { VariationType } from '@/models/Programme/VariationType'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import TutorialVideoDialog from '@/components/app/TutorialVideoDialog.vue'

const props = defineProps({
  page: {
    type: String as PropType<PagesWithVideo>,
    required: true,
  },
})

const appStore = useAppStore()
const authStore = useAuthStore()

const dialogRerenderKey = ref<number>(0)
const tutorialDialog = ref(false)

const videoUrl = computed(() => {
  const tutorialVideos = appStore.settings.TUTORIAL_VIDEOS
  const pageVideos = tutorialVideos?.[props.page]

  if (pageVideos && authStore.organisationRegionVariationType) {
    return pageVideos[authStore.organisationRegionVariationType] ?? pageVideos[VariationType.MyCC]
  }

  return ''
})

const onPreviewClick = () => {
  dialogRerenderKey.value++
  tutorialDialog.value = true
}
</script>

<template>
  <div v-if="videoUrl">
    <div
      class="tutorial-preview cursor-pointer rounded overflow-hidden"
      @click="onPreviewClick"
    >
      <iframe
        :src="`${videoUrl}&badge=0&autopause=0&autoplay=0&controls=0`"
        class="tutorial-preview__iframe"
        frameborder="0"
      />
      <div class="tutorial-preview__overlay d-flex align-center justify-center">
        <VIcon
          size="x-large"
          color="white"
        >
          mdi-play-circle
        </VIcon>
      </div>
    </div>
    <TutorialVideoDialog
      :key="dialogRerenderKey"
      :page="page"
      :model="tutorialDialog"
    />
  </div>
</template>

<style lang="scss" scoped>
.tutorial-preview {
  position: relative;
  aspect-ratio: 16 / 9;
  max-width: 15rem;

  &__iframe {
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
    transition: background 0.2s ease;
  }

  &:hover &__overlay {
    background: rgba(0, 0, 0, 0.15);
  }
}
</style>
